/**
 * FISH SPAWN/RARITY SYSTEM — the fishing analogue of the insect spawn system (sim/insects.ts). A CAST classifies
 * the water (pond/stream), then this module decides WHICH fish bites (or NO bite) using the same standardized,
 * inspectable model: hard GATES + soft DRIVERS → an effective weight → a SOFT-capped absolute per-cast chance.
 *
 * Everything is admin-editable + inspectable in the Fish Odds modal (the fish twin of the Insect Odds modal),
 * and persisted in the live-tuning keys `fishConditions` / `fishRarity` / `fishSpawn` (see main.tsx installer).
 *
 * WHY the same model: a rare fish must NOT bite every cast just because it's the only eligible one in that water,
 * yet a player who optimizes the pond FOR it can raise their odds (with diminishing returns, no hard ceiling).
 * The "leftover" probability of a cast = NO BITE (the fishing equivalent of the insects' "spawn nothing").
 */

import { FISH, BITE_MIN_MS, BITE_MAX_MS, type FishDef, type WaterHabitat } from "./fishing";
import type { WeatherType } from "./weather";
import { isOut } from "./releases";
import { scoreRequireSets, type ReqCtx } from "./requireSets";
import { FISH_HOST_SETS, withHostSets } from "./hostSets";
import { DEFAULT_FISH_CONDS } from "./fishConds";
import { coerceCondMap, coerceRarityMap, coerceTierTable, finiteAtLeast, isPlainObject, type CondSpec } from "./tuningCoerce";

export type FishRarity = "common" | "uncommon" | "rare" | "epic" | "legendary";
export const FISH_RARITIES: FishRarity[] = ["common", "uncommon", "rare", "epic", "legendary"];

/** A data-driven per-cast condition, evaluated by fishSpawnWeight. GATES (pass/fail) + DRIVERS (soft scaling).
 *  Mirrors the insect AppearCond but the signals that matter for fishing (water, weather, garden composition). */
export interface FishCond {
  // ── GATES ──
  night?: boolean;                 // only at night (phase ≥ DUSK_END)
  day?: boolean;                   // only in daylight (the broad day segment, i.e. anything not night)
  dawn?: boolean;                  // only in the DAWN twilight window (phase < DAWN_END) — ~5 real minutes
  dusk?: boolean;                  // only in the DUSK twilight window (DAY_END ≤ phase < DUSK_END) — ~5 real minutes
  rainMin?: number;                // rain intensity ≥ this. ONLY three values are real: 0.35 any rain, 0.8 heavy, 1 storm — see fishConds.ts RAIN_ANY/RAIN_HEAVY/RAIN_STORM
  weather?: WeatherType[];         // current weather ∈ this set
  minWaterCells?: number;          // needs ≥ N water cells in the garden (a big body of water)
  minPlants?: number;              // needs ≥ N plants total (a lush garden)
  minFlowering?: number;           // needs ≥ N flowering plants
  minPlots?: number;               // needs ≥ N purchased plot cells
  decorId?: string; decorMin?: number;   // needs ≥ N of a decor item placed
  speciesId?: string; speciesMin?: number;   // needs ≥ N of a plant species
  clusterSpecies?: string; clusterMin?: number; clusterRadius?: number;   // ≥N of a species clustered within R
  // ── WATER-SHAPE GATES (the CAST body — see sim/waterType.ts waterShapeAt) ──
  pondAreaMin?: number;            // the cast pond must be ≥ this many cells (a big-water fish)
  pondAreaMax?: number;            // the cast pond must be ≤ this many cells (a small-pool fish)
  streamLenMin?: number;          // the cast stream channel must be ≥ this long (real flowing water)
  // ── TACKLE GATES (the player's gear — see sim/fishingGear.ts) ──
  needsPower?: number;             // tackle POWER must be ≥ this to hook it (a stronger rig)
  needsLure?: string;              // a lure of this FAMILY must be equipped (some fish bite only one lure)
  // ── DRIVERS (soft multipliers) ──
  likesWater?: number;             // scales with water-cell count (water-loving fish)
  likesPlants?: number;            // scales with total plant count
  likesFlowers?: number;           // scales with flowering-plant count
  // ── REQUIRE-SETS (unified attractor model; host plants / decor / clusters as gate and/or driver) ──
  sets?: import("./requireSets").RequireSet[];
}

/** The four segments of the day/night cycle (see render/dayNight.ts, which owns the phase boundaries). */
export type DaySegment = "dawn" | "day" | "dusk" | "night";

/** The live context a fish condition is scored against — built at cast time from the garden + the cast's water. */
export interface FishCtx {
  water: WaterHabitat;             // the classified water the player cast into ("pond" | "stream")
  waterCells: number;              // total water cells in the garden
  pondArea: number;                // cells in the CAST body's pond region (0 if none) — for pondArea gates
  streamLength: number;            // longest channel extent in the cast body (0 if none) — for streamLen gates
  tacklePower: number;             // the player's derived tackle POWER (from equipped rig)
  lure?: string;                   // the equipped lure family id (for needsLure gates)
  plantCount: number;
  floweringCount: number;
  plotCount: number;
  night: boolean;
  /** which quarter of the day/night cycle the cast happens in — the `dawn`/`dusk` gates read this, while
   *  `night`/`day` stay on the coarse boolean above (dusk counts as day for those, as it always has). */
  daySegment: DaySegment;
  rain: number;
  weather: WeatherType;
  speciesCounts: Record<string, number>;
  plantPos: Array<{ species: string; gx: number; gy: number }>;
  decorCounts: Record<string, number>;
}

export interface FishScore { weight: number; eligible: boolean; base: number; driverScale: number; reasons: string[]; }

// ── GLOBAL FISH SPAWN CONFIG (the `fishSpawn` tuning key) ──
export interface FishSpawnConfig {
  // DIRECT-% MODEL: each fish has a BASE % (per-cast bite chance in a neutral garden); drivers raise it toward
  // the tier ceiling with diminishing returns.
  capSoftness: number;             // driver climb difficulty (higher = slower approach to the ceiling)
  capWater: number; capPlants: number; capFlowers: number;   // soft caps on the drivers
  tierBasePct: Record<FishRarity, number>;   // default base % per tier
  tierCap: Record<FishRarity, number>;        // per-tier SOFT ceiling drivers asymptote toward (0..1)
}
export const DEFAULT_FISH_SPAWN_CONFIG: FishSpawnConfig = {
  capSoftness: 6,
  capWater: 20, capPlants: 20, capFlowers: 12,
  tierBasePct: { common: 0.4, uncommon: 0.22, rare: 0.12, epic: 0.06, legendary: 0.02 },
  tierCap:     { common: 1, uncommon: 0.8, rare: 0.5, epic: 0.3, legendary: 0.15 },
};
/** The smallest value each numeric knob may take — the Fish Odds modal's `min` on each input, enforced here for
 *  the same reason as the insect twin (sim/insects.ts SPAWN_CONFIG_MIN): `<input min>` does not stop an
 *  emptied box arriving as 0, and a non-number from a hand-edited blob keeps the default. */
const FISH_CONFIG_MIN: Record<Exclude<keyof FishSpawnConfig, "tierBasePct" | "tierCap">, number> = {
  capSoftness: 1, capWater: 1, capPlants: 1, capFlowers: 1,
};
let FISH_CONFIG: FishSpawnConfig = { ...DEFAULT_FISH_SPAWN_CONFIG };
export function setFishSpawnConfig(cfg: Partial<FishSpawnConfig> | undefined): void {
  const o: Record<string, unknown> = isPlainObject(cfg) ? cfg : {};
  const next = { ...DEFAULT_FISH_SPAWN_CONFIG };
  for (const k of Object.keys(FISH_CONFIG_MIN) as Array<keyof typeof FISH_CONFIG_MIN>) {
    next[k] = finiteAtLeast(o[k], FISH_CONFIG_MIN[k], DEFAULT_FISH_SPAWN_CONFIG[k]);
  }
  next.tierBasePct = coerceTierTable(o.tierBasePct, DEFAULT_FISH_SPAWN_CONFIG.tierBasePct);
  next.tierCap = coerceTierTable(o.tierCap, DEFAULT_FISH_SPAWN_CONFIG.tierCap);
  FISH_CONFIG = next;
}
export function getFishSpawnConfig(): FishSpawnConfig { return FISH_CONFIG; }

// ── PER-FISH OVERRIDES (the `fishConditions` + `fishRarity` tuning keys) ──
export type FishConditionOverrides = Record<string, FishCond | null>;
/** FishCond's non-numeric fields, for `coerceCondMap` — every other field is a number. A `null` entry is kept:
 *  it is the admin's explicit "no conditions". */
const FISH_COND_SPEC: CondSpec = {
  bools: ["night", "day", "dawn", "dusk"],
  strings: ["decorId", "speciesId", "clusterSpecies", "needsLure"],
  stringArrays: ["weather"],
};
let COND_OVERRIDES: FishConditionOverrides = {};
export function setFishConditionOverrides(m: FishConditionOverrides | undefined): void { COND_OVERRIDES = coerceCondMap<FishCond>(m, FISH_COND_SPEC); }
export function getFishConditionOverrides(): FishConditionOverrides { return COND_OVERRIDES; }

export type FishRarityOverrides = Record<string, { rarity?: FishRarity; basePct?: number } | null>;
let RARITY_OVERRIDES: FishRarityOverrides = {};
export function setFishRarityOverrides(m: FishRarityOverrides | undefined): void { RARITY_OVERRIDES = coerceRarityMap(m, FISH_RARITIES); }
export function getFishRarityOverrides(): FishRarityOverrides { return RARITY_OVERRIDES; }

// PER-FISH VALUE/STAMINA OVERRIDES (the `fishValue` tuning key) — applied from the Fish Odds tool's value-model
// suggestions. When present they win over the static FISH def, so an admin can retune the fishing economy live.
export type FishValueOverrides = Record<string, { value?: number; difficulty?: number } | null>;
let VALUE_OVERRIDES: FishValueOverrides = {};
export function setFishValueOverrides(m: FishValueOverrides | undefined): void { VALUE_OVERRIDES = (m && typeof m === "object") ? m : {}; }
export function getFishValueOverrides(): FishValueOverrides { return VALUE_OVERRIDES; }
/** A fish's EFFECTIVE coin value: `fishValue` override → static def value. */
export function fishValueOf(def: FishDef): number {
  const o = VALUE_OVERRIDES[def.id];
  if (o && typeof o.value === "number" && isFinite(o.value)) return Math.max(1, Math.round(o.value));
  return def.value;
}
/** A fish's EFFECTIVE fight difficulty/stamina (0..1): `fishValue` override → static def difficulty. */
export function fishDifficultyOf(def: FishDef): number {
  const o = VALUE_OVERRIDES[def.id];
  if (o && typeof o.difficulty === "number" && isFinite(o.difficulty)) return Math.max(0, Math.min(1, o.difficulty));
  return def.difficulty;
}

/** The effective per-fish condition. An admin `fishConditions` override wins entirely; otherwise it's the
 *  authored DEFAULT condition (water shape / power / lure / time — see fishConds.ts) merged with the fish's
 *  default host-plant attractor SETS (FISH_HOST_SETS).
 *  The two set sources are APPENDED, not replaced — same as the insect resolver (effectiveAppears). This used
 *  to spread `{ sets: host }` over the default, which silently DISCARDED the inline `sets` of any fish that
 *  gained a host set; six fish carry inline sets today and were safe only by accident. */
export function effectiveFishCond(def: FishDef): FishCond | undefined {
  if (Object.prototype.hasOwnProperty.call(COND_OVERRIDES, def.id)) return COND_OVERRIDES[def.id] ?? undefined;
  return authoredFishCond(def);
}
/** The AUTHORED condition — what the bundle ships, ignoring any live override. The dev override report compares
 *  against it. */
export function authoredFishCond(def: FishDef): FishCond | undefined {
  const dflt = DEFAULT_FISH_CONDS[def.id];
  const host = FISH_HOST_SETS[def.id];
  if (!dflt && !host?.length) return undefined;
  const sets = withHostSets(dflt?.sets, host);
  return { ...(dflt ?? {}), ...(sets ? { sets } : {}) };
}
/** The spawn sim's tier for a weight — the SINGLE derivation of a fish's rarity. The Field Guide reads it
 *  too, through `fishRarityOf` (so an admin rarity override moves the badge as well as the odds); a second,
 *  rank-based derivation used to sit beside this one and drift out of agreement with it. Each threshold
 *  sits in the gap between two bands of the weight ladder documented in sim/fishing.ts — do not nudge one
 *  without re-checking that ladder. ("epic" is the spawn sim's name for the guide's "Very Rare".) */
export function inferFishRarity(weight: number): FishRarity {
  if (weight >= 82) return "common";
  if (weight >= 62) return "uncommon";
  if (weight >= 38) return "rare";
  if (weight >= 19) return "epic";
  return "legendary";
}
export function fishRarityOf(def: FishDef): FishRarity {
  const o = RARITY_OVERRIDES[def.id];
  if (o && o.rarity) return o.rarity;
  return inferFishRarity(def.weight);
}
/** The EFFECTIVE BASE % (0..1) for a fish in a neutral garden: admin basePct override → tier default base %. */
export function baseFishChanceOf(def: FishDef): number {
  const o = RARITY_OVERRIDES[def.id];
  if (o && typeof o.basePct === "number") return Math.max(0, Math.min(1, o.basePct));
  return FISH_CONFIG.tierBasePct[fishRarityOf(def)] ?? 0.1;
}

/** DRIVER CURVE — base % raised toward `ceiling` by `driverScale` with diminishing returns (see insects.driverCurve). */
export function fishDriverCurve(base: number, driverScale: number, ceiling: number, soften: number): number {
  const b = Math.max(0, Math.min(1, base));
  if (driverScale <= 1 || b >= ceiling) return b;
  const bonus = b * (driverScale - 1), room = ceiling - b, k = Math.max(0.01, soften);
  return b + room * (1 - Math.exp(-bonus / (room * k)));
}

/** Per-cast REAL-TIME budget (seconds) for the "expected time to catch" estimate: the average bite wait
 *  (BITE_MIN..BITE_MAX midpoint) + a fixed hook/fight allowance + the reward/reset time before the next cast. */
export const CAST_HOOK_S = 15;     // player hooks + fights the fish
export const CAST_REWARD_S = 3;    // reward shown + ready to cast again
// DERIVED, not copied. This used to restate the window as literals and silently went stale the first time
// fishing.ts was retuned; it now reads the real constants (currently 4200..13200 → 8.7s). It is the BASE-STAT
// average — gear divides the actual wait, so the estimate is deliberately the pessimistic end.
export const CAST_BITE_AVG_S = (BITE_MIN_MS + BITE_MAX_MS) / 2 / 1000;
export function secondsPerCast(): number { return CAST_BITE_AVG_S + CAST_HOOK_S + CAST_REWARD_S; }
/** Expected CASTS until an event of per-cast probability `chance` occurs (= 1/chance), and the real seconds. */
export function expectedCastsToAppear(chance: number): { casts: number; seconds: number } {
  if (chance <= 0) return { casts: Infinity, seconds: Infinity };
  const casts = 1 / chance;
  return { casts, seconds: casts * secondsPerCast() };
}

/** REALIZED per-cast chance accounting for competition with every other eligible fish (same model as insects):
 *  Σp ≤ 1 → own chance; Σp > 1 → chance · (1/Σp). */
export function realizedFishChance(chance: number, totalChance: number): number {
  if (chance <= 0) return 0;
  return totalChance > 1 ? chance / totalChance : chance;
}
/** Σ of every fish's absolute per-cast chance in `ctx` (UNCLAMPED). */
export function totalRawBiteChance(ctx: FishCtx): number {
  return FISH.reduce((s, def) => s + fishAbsoluteChance(def, ctx), 0);
}

function biggestCluster(plants: Array<{ species: string; gx: number; gy: number }>, species: string, radius: number): number {
  const pts = plants.filter((p) => p.species === species);
  if (pts.length <= 1) return pts.length;
  const r2 = radius * radius; let best = 1;
  for (let i = 0; i < pts.length; i++) { let n = 1; for (let j = 0; j < pts.length; j++) { if (i === j) continue; const dx = pts[i]!.gx - pts[j]!.gx, dy = pts[i]!.gy - pts[j]!.gy; if (dx * dx + dy * dy <= r2) n++; } if (n > best) best = n; }
  return best;
}

/** Score one fish for the current cast context. HABITAT is a hard prerequisite (a pond fish never bites a stream). */
export function fishSpawnWeight(def: FishDef, ctx: FishCtx): FishScore {
  const base = baseFishChanceOf(def);   // the fish's BASE % (0..1) in a neutral garden
  const reasons: string[] = [];
  // HABITAT gate (always applies, independent of conditions).
  if (def.habitat !== "both" && def.habitat !== ctx.water) {
    return { weight: 0, eligible: false, base, driverScale: 0, reasons: [`${def.habitat}-only ✗ (cast in ${ctx.water})`] };
  }
  reasons.push(`${def.habitat === "both" ? "any water" : def.habitat} ✓`);
  const c = effectiveFishCond(def);
  if (!c) return { weight: base, eligible: true, base, driverScale: 1, reasons };

  let eligible = true; const fail = (m: string) => { eligible = false; reasons.push(m); };
  if (c.night) (ctx.night ? reasons.push("night ✓") : fail("night ✗"));
  if (c.day) (!ctx.night ? reasons.push("day ✓") : fail("day ✗ (night)"));
  if (c.dawn) (ctx.daySegment === "dawn" ? reasons.push("dawn ✓") : fail(`dawn ✗ (${ctx.daySegment})`));
  if (c.dusk) (ctx.daySegment === "dusk" ? reasons.push("dusk ✓") : fail(`dusk ✗ (${ctx.daySegment})`));
  if (c.rainMin != null) (ctx.rain >= c.rainMin ? reasons.push(`rain≥${c.rainMin} ✓`) : fail(`rain≥${c.rainMin} ✗`));
  if (c.weather) (c.weather.includes(ctx.weather) ? reasons.push(`weather ${ctx.weather} ✓`) : fail(`weather ✗`));
  if (c.minWaterCells != null) (ctx.waterCells >= c.minWaterCells ? reasons.push(`≥${c.minWaterCells} water ✓`) : fail(`≥${c.minWaterCells} water ✗ (${ctx.waterCells})`));
  // WATER-SHAPE gates (the cast body).
  if (c.pondAreaMin != null) (ctx.pondArea >= c.pondAreaMin ? reasons.push(`pond≥${c.pondAreaMin} ✓`) : fail(`needs a bigger pond (≥${c.pondAreaMin}, this ${ctx.pondArea}) ✗`));
  if (c.pondAreaMax != null) (ctx.pondArea <= c.pondAreaMax ? reasons.push(`pond≤${c.pondAreaMax} ✓`) : fail(`needs a smaller pool (≤${c.pondAreaMax}, this ${ctx.pondArea}) ✗`));
  if (c.streamLenMin != null) (ctx.streamLength >= c.streamLenMin ? reasons.push(`stream≥${c.streamLenMin} ✓`) : fail(`needs a longer stream (≥${c.streamLenMin}, this ${ctx.streamLength}) ✗`));
  // TACKLE gates (the player's gear).
  if (c.needsPower != null) (ctx.tacklePower >= c.needsPower ? reasons.push(`power≥${c.needsPower} ✓`) : fail(`needs stronger tackle (power ≥${c.needsPower}, you ${ctx.tacklePower}) ✗`));
  if (c.needsLure) (ctx.lure === c.needsLure ? reasons.push(`lure ${c.needsLure} ✓`) : fail(`needs a ${c.needsLure} lure ✗`));
  if (c.minPlants != null) (ctx.plantCount >= c.minPlants ? reasons.push(`≥${c.minPlants} plants ✓`) : fail(`≥${c.minPlants} plants ✗`));
  if (c.minFlowering != null) (ctx.floweringCount >= c.minFlowering ? reasons.push(`≥${c.minFlowering} flowering ✓`) : fail(`≥${c.minFlowering} flowering ✗`));
  if (c.minPlots != null) (ctx.plotCount >= c.minPlots ? reasons.push(`≥${c.minPlots} plots ✓`) : fail(`≥${c.minPlots} plots ✗`));
  if (c.decorId && c.decorMin != null) { const h = ctx.decorCounts[c.decorId] ?? 0; (h >= c.decorMin ? reasons.push(`≥${c.decorMin} ${c.decorId} ✓`) : fail(`≥${c.decorMin} ${c.decorId} ✗ (${h})`)); }
  if (c.speciesId && c.speciesMin != null) { const h = ctx.speciesCounts[c.speciesId] ?? 0; (h >= c.speciesMin ? reasons.push(`≥${c.speciesMin} ${c.speciesId} ✓`) : fail(`≥${c.speciesMin} ${c.speciesId} ✗ (${h})`)); }
  if (c.clusterSpecies && c.clusterMin != null && c.clusterRadius != null) { const b = biggestCluster(ctx.plantPos, c.clusterSpecies, c.clusterRadius); (b >= c.clusterMin ? reasons.push(`${c.clusterMin}× ${c.clusterSpecies}@${c.clusterRadius} ✓`) : fail(`${c.clusterMin}× ${c.clusterSpecies}@${c.clusterRadius} ✗ (max ${b})`)); }
  // REQUIRE-SETS (unified attractor gates)
  const reqCtx: ReqCtx = { speciesCounts: ctx.speciesCounts, decorCounts: ctx.decorCounts, plantPos: ctx.plantPos };
  const rs = scoreRequireSets(c.sets, reqCtx, FISH_CONFIG.capFlowers);
  reasons.push(...rs.reasons);
  if (!rs.eligible) eligible = false;
  if (!eligible) return { weight: 0, eligible: false, base, driverScale: 0, reasons };

  let scale = rs.driver;
  if (c.likesWater) { const f = 1 + c.likesWater * Math.min(ctx.waterCells, FISH_CONFIG.capWater); scale *= f; reasons.push(`water ${ctx.waterCells} → ×${f.toFixed(2)}`); }
  if (c.likesPlants) { const f = 1 + c.likesPlants * Math.min(ctx.plantCount, FISH_CONFIG.capPlants); scale *= f; reasons.push(`plants ${ctx.plantCount} → ×${f.toFixed(2)}`); }
  if (c.likesFlowers) { const f = 1 + c.likesFlowers * Math.min(ctx.floweringCount, FISH_CONFIG.capFlowers); scale *= f; reasons.push(`flowers ${ctx.floweringCount} → ×${f.toFixed(2)}`); }
  return { weight: base * scale, eligible: true, base, driverScale: scale, reasons };
}

/** The absolute per-CAST bite chance — DIRECT-% model: base % in a neutral garden, drivers raise toward the
 *  tier ceiling with diminishing returns. */
export function fishAbsoluteChance(def: FishDef, ctx: FishCtx): number {
  if (isOut(def)) return 0;   // never bites, shadows or shows in the odds (a content release that is not out yet — sim/releases.ts)
  const s = fishSpawnWeight(def, ctx);
  if (!s.eligible || s.base <= 0) return 0;
  const ceiling = FISH_CONFIG.tierCap[fishRarityOf(def)] ?? 1;
  return fishDriverCurve(s.base, s.driverScale, ceiling, FISH_CONFIG.capSoftness);
}

/** Per-tier luck weight — how much a `luck` bonus lifts each rarity. Commons barely move; rares/legendaries
 *  get the boost (luck buys you a shot at the good stuff, not more minnows).
 *
 *  THE ONE TABLE. Exported because the fish-shadow bite (`fishShadows.ts`) and the progression sim
 *  (`fishProgressionSim.ts`) both weight by luck and must not keep private copies — three tables drifted
 *  apart once already and the shadow hover card ended up quoting odds an open-water cast would beat. The
 *  shadow path scales these by a documented ratio rather than re-listing them; see `SHADOW_LUCK_RATIO`. */
export const LUCK_TIER: Record<FishRarity, number> = { common: 0, uncommon: 0.4, rare: 1, epic: 1.6, legendary: 2.2 };

/** THE OPEN-WATER LUCK LIFT: a fish's absolute chance after the gear's `luck` (≥1) tilts it by rarity. The
 *  catch roll (`pickFishByChance`) and the bottom-left odds viewer (`biteOdds(…, "water")`) both call THIS,
 *  so the viewer cannot quote odds the roll does not use — they were two copies of one formula. */
export function luckLift(def: FishDef, chance: number, luck: number): number {
  return chance > 0 && luck > 1 ? chance * (1 + (luck - 1) * LUCK_TIER[fishRarityOf(def)]) : chance;
}

/** Pick a fish for a cast using ABSOLUTE chances. Returns null = NO BITE (the leftover probability), so even a
 *  lone legendary in that water doesn't bite every cast. `luck` (≥1, from gear) tilts the WHICH-fish roll
 *  toward rarer species without changing whether anything bites. */
export function pickFishByChance(ctx: FishCtx, roll: number, luck = 1): FishDef | null {
  const raw = FISH.map((def) => ({ def, raw: fishAbsoluteChance(def, ctx) })).filter((s) => s.raw > 0);
  // whether SOMETHING bites uses the UN-luck-boosted total (luck shouldn't make casts land more often, only
  // shift which fish); luck then lifts rarer fish in the which-fish split.
  const rawSum = raw.reduce((s, x) => s + x.raw, 0);
  return rollBite(raw.map((s) => ({ def: s.def, p: luckLift(s.def, s.raw, luck) })), rawSum, roll);
}

/** THE ROLL ITSELF, shared by the real cast (`pickFishByChance`) and the Fish Odds modal's 🎲 Simulate
 *  (`simulateCasts`). `roll` < min(1, rawSum) bites; the bite is then split across `live` by its `p`. The
 *  simulator used to carry its own copy of these lines, so "simulate" proved a second implementation agreed
 *  with the odds column rather than proving the game's roll did. */
function rollBite(live: Array<{ def: FishDef; p: number }>, rawSum: number, roll: number): FishDef | null {
  if (!live.length) return null;
  const pBite = Math.min(1, rawSum);
  if (roll >= pBite) return null;
  const sum = live.reduce((s, x) => s + x.p, 0);
  let x = (roll / pBite) * sum;
  for (const s of live) { if ((x -= s.p) <= 0) return s.def; }
  return live[live.length - 1]!.def;
}

/** Full scored roster for the current context (for the Fish Odds modal). */
export function scoreFishRoster(ctx: FishCtx): Array<{ def: FishDef; score: FishScore; chance: number; rarity: FishRarity }> {
  return FISH
    .map((def) => ({ def, score: fishSpawnWeight(def, ctx), chance: fishAbsoluteChance(def, ctx), rarity: fishRarityOf(def) }))
    .sort((a, b) => b.chance - a.chance || b.score.weight - a.score.weight);
}

export interface FishSimResult {
  attempts: number; bit: number; empty: number;
  perSpecies: Array<{ id: string; name: string; icon: string; rarity: FishRarity; observed: number; expected: number; chance: number }>;
}
/** Simulate `attempts` casts against the current context/settings; report actual vs. expected per fish.
 *  `expected` uses the REALIZED per-cast rate (competition-adjusted: chance·min(1,Σ)/Σ), NOT the raw chance —
 *  so it matches how the sim actually shares bites when the garden is oversubscribed (Σ>1). */
export function simulateCasts(ctx: FishCtx, attempts: number, rng: () => number = Math.random): FishSimResult {
  const chances = FISH.map((def) => ({ def, p: fishAbsoluteChance(def, ctx) }));
  const live0 = chances.filter((c) => c.p > 0);
  const sum0 = live0.reduce((s, x) => s + x.p, 0);
  const realizeK = sum0 > 0 ? Math.min(1, sum0) / sum0 : 0;   // each species' realized rate = p · realizeK
  const observed: Record<string, number> = {}; let bit = 0;
  for (let i = 0; i < attempts; i++) {
    const picked = rollBite(live0, sum0, rng());   // the cast's own roll, at luck 1 (chances computed once)
    if (!picked) continue;
    observed[picked.id] = (observed[picked.id] ?? 0) + 1; bit++;
  }
  const perSpecies = live0
    .map((c) => ({ id: c.def.id, name: c.def.name, icon: c.def.icon, rarity: fishRarityOf(c.def), observed: observed[c.def.id] ?? 0, expected: attempts * c.p * realizeK, chance: c.p }))
    .sort((a, b) => b.observed - a.observed || b.expected - a.expected);
  return { attempts, bit, empty: attempts - bit, perSpecies };
}
